import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Eyebrow from "@/components/site/Eyebrow";
import { ACCENT, NAVY, CREAM } from "@/lib/siteTheme";
import heronIcon from "@/assets/design/blue-heron-icon.webp";

import stumpprosBefore from "@/assets/design/stumppros-grid-before.webp";
import stumpprosAfter from "@/assets/design/stumppros-grid-after.webp";
import autolabBefore from "@/assets/design/autolab-grid-before.webp";
import autolabAfter from "@/assets/design/autolab-grid-after.webp";
import dndBefore from "@/assets/design/dnd-grid-before.webp";
import dndAfter from "@/assets/design/dnd-grid-after.webp";
import limetruckBefore from "@/assets/design/limetruck-grid-before.webp";
import limetruckAfter from "@/assets/design/limetruck-grid-after.webp";

import siteAttorney from "@/assets/design/site-attorney.webp";
import siteBarbershop from "@/assets/design/site-barbershop.webp";
import siteChiropractor from "@/assets/design/site-chiropractor.webp";
import siteDumpster from "@/assets/design/site-dumpster.webp";
import siteFoodtruck from "@/assets/design/site-foodtruck.webp";
import siteGym from "@/assets/design/site-gym.webp";
import siteHvac from "@/assets/design/site-hvac.webp";
import siteInstallation from "@/assets/design/site-installation.webp";
import siteInsurance from "@/assets/design/site-insurance.webp";
import siteLawncare from "@/assets/design/site-lawncare.webp";
import siteMortgage from "@/assets/design/site-mortgage.webp";
import siteMoving from "@/assets/design/site-moving.webp";
import siteStump from "@/assets/design/site-stump.webp";
import siteTherapist from "@/assets/design/site-therapist.webp";
import siteTinyhomes from "@/assets/design/site-tinyhomes.webp";
import siteTravel from "@/assets/design/site-travel.webp";
import siteTree from "@/assets/design/site-tree.webp";
import siteWellness from "@/assets/design/site-wellness.webp";
import siteWindow from "@/assets/design/site-window.webp";
import siteWinery from "@/assets/design/site-winery.webp";

type ResultGridItem = { value: string; label: string; sub: string };
type StatItem = { value: string; label: string };
type ResultStat = { value: string; label: string; delta: string; period: string };

interface CaseStudy {
  tag: string;
  title: string;
  situation: string;
  problem: string;
  solution: string;
  to: number;
  prefix: string;
  suffix: string;
  resultLabel: string;
  maps?: [string, string];
  resultGrid?: ResultGridItem[];
  resultGridLabel?: string;
  beforeStats?: StatItem[];
  afterStats?: StatItem[];
  stats?: ResultStat[];
  quote?: { name: string; text: string };
}

const seoStudies: CaseStudy[] = [
  {
    tag: "Local SEO",
    title: "Stump Pros",
    situation: "A stump removal company relying on word of mouth in a service area with steady competition.",
    problem: "It barely appeared in the local map pack and ranked below competitors for high-intent searches like stump removal near me.",
    solution: "Local SEO buildout with Google Business Profile optimization, service-area pages, and a consistent review system.",
    to: 284, prefix: "", suffix: "%", resultLabel: "Increase in organic leads over six months.",
    maps: [stumpprosBefore, stumpprosAfter],
  },
  {
    tag: "Local SEO",
    title: "Auto Lab",
    situation: "An auto repair shop wanting to rank for its specialty services well beyond its immediate neighborhood.",
    problem: "They needed more first time customers, but they were sitting low in the local map pack across most of their service area.",
    solution: "Google Business Profile optimization, citation building, and our secret sauce.",
    to: 40, prefix: "", suffix: "%", resultLabel: "Increase in phone calls and form submissions year over year.",
    maps: [autolabBefore, autolabAfter],
    resultGrid: [
      { value: "413", label: "Total tracked calls", sub: "303 → 413" },
      { value: "143", label: "First time callers", sub: "117 → 143" },
      { value: "193", label: "Calls from Google Business Profile", sub: "155 → 193" },
      { value: "+36%", label: "Call volume", sub: "Year over year" },
    ],
    resultGridLabel: "Tracked calls, year over year",
  },
  {
    tag: "Local SEO",
    title: "D&D Movers",
    situation: "A moving company competing against national van lines for local search visibility.",
    problem: "Its site had little location-specific content and lost local searches to far bigger brands.",
    solution: "Local SEO with city-level moving pages, structured data, and a review generation workflow.",
    to: 5, prefix: "", suffix: "", resultLabel: "Month streak of the biggest revenue months in company history.",
    maps: [dndBefore, dndAfter],
    stats: [
      { value: "4,249", label: "People viewed their Business Profile", delta: "121.3%", period: "year over year" },
      { value: "1,093", label: "Searches surfaced their Business Profile", delta: "406.0%", period: "year over year" },
    ],
    quote: { name: "Alyson Mullins", text: "Luke and his team have been a game changer for our website and marketing strategies. From the start we set goals for our SEO optimization and internet advertising and he has been key in helping us reach those goals and then some." },
  },
  {
    tag: "Local SEO",
    title: "The Lime Truck",
    situation: "A well-known food truck and catering brand wanting to own local catering searches.",
    problem: "Its catering pages were not optimized, so booking inquiries came almost entirely from social, not search.",
    solution: "SEO-focused catering landing pages, event-type targeting, and structured data for menus and service areas.",
    to: 3, prefix: "", suffix: "", resultLabel: "Month streak of the biggest booked revenue in company history.",
    maps: [limetruckBefore, limetruckAfter],
  },
];

const adsStudies: CaseStudy[] = [
  {
    tag: "Google Ads",
    title: "The Lime Truck",
    situation: "A high-profile food truck and catering operation expanding its private and corporate event bookings.",
    problem: "Catering demand was seasonal and unpredictable, with no paid channel to smooth it out.",
    solution: "Google Ads campaigns targeting corporate and private event keywords, with conversion tracking on every inquiry form.",
    to: 50, prefix: "-", suffix: "%", resultLabel: "Lower cost per acquisition, on 38% less ad spend.",
    beforeStats: [
      { value: "4.38K", label: "Clicks" }, { value: "$24.5K", label: "Cost" },
      { value: "186.53", label: "Conversions" }, { value: "$131", label: "Cost / conv." },
    ],
    afterStats: [
      { value: "3.19K", label: "Clicks" }, { value: "$15.2K", label: "Cost" },
      { value: "229.67", label: "Conversions" }, { value: "$66.21", label: "Cost / conv." },
    ],
    resultGrid: [
      { value: "+23%", label: "More conversions", sub: "186 → 229" },
      { value: "-50%", label: "Lower CPA", sub: "$131 → $66" },
      { value: "-38%", label: "Less ad spend", sub: "$24.5K → $15.2K" },
      { value: "$9.3K", label: "Saved per month", sub: "More leads for less" },
    ],
  },
  {
    tag: "Google Ads",
    title: "Auto Lab",
    situation: "An auto repair shop running Google Ads that wanted more booked service without raising its budget.",
    problem: "There was no call tracking in place, so no way to prove which ads drove booked service.",
    solution: "A rebuilt Google Ads account focused on their highest value services, wired to CallRail so every lead traced back to a keyword.",
    to: 32, prefix: "+", suffix: "%", resultLabel: "More conversions year over year, on a fraction of the impressions.",
    beforeStats: [
      { value: "159", label: "Clicks" }, { value: "4.69K", label: "Impressions" },
      { value: "$16.43", label: "Cost / conv." }, { value: "22.00", label: "Conversions" },
    ],
    afterStats: [
      { value: "88", label: "Clicks" }, { value: "987", label: "Impressions" },
      { value: "$16.09", label: "Cost / conv." }, { value: "29.00", label: "Conversions" },
    ],
    resultGrid: [
      { value: "+32%", label: "More conversions", sub: "22 → 29" },
      { value: "$16.09", label: "Cost / conv.", sub: "Held steady" },
      { value: "-79%", label: "Wasted impressions", sub: "4.69K → 987" },
      { value: "↑", label: "Conversion rate", sub: "Tighter targeting" },
    ],
  },
  {
    tag: "Google Ads",
    title: "D&D Movers",
    situation: "A moving company with strong organic visibility that wanted a second, controllable source of demand.",
    problem: "They had no Google Ads presence at all, so there was no way to turn demand up during slower weeks.",
    solution: "A Google Ads account built from scratch, with tightly themed ad groups, negative keyword lists, and conversion tracking on every call and form.",
    to: 34, prefix: "", suffix: "", resultLabel: "Conversions in the first seven weeks, from a standing start.",
    resultGrid: [
      { value: "34", label: "Conversions", sub: "First 7 weeks" },
      { value: "$27.61", label: "Cost / conv.", sub: "From launch" },
      { value: "101", label: "Clicks", sub: "From 1.23K impressions" },
      { value: "8.2%", label: "Click-through rate", sub: "Tightly themed ad groups" },
    ],
  },
  {
    tag: "Google Ads",
    title: "Lakeland Dumpster Rentals",
    situation: "A dumpster rental company competing on availability and price in a busy local market.",
    problem: "They had no prior ad history, so every rental had to come from organic demand or word of mouth.",
    solution: "A Google Ads account built from scratch with tightly themed ad groups, negative keyword lists, and call tracking on every lead.",
    to: 29, prefix: "", suffix: "", resultLabel: "Conversions in the first eight weeks, from a standing start.",
    resultGrid: [
      { value: "29", label: "Conversions", sub: "First 8 weeks" },
      { value: "201", label: "Clicks", sub: "From 1.26K impressions" },
      { value: "16%", label: "Click-through rate", sub: "High intent targeting" },
      { value: "$42.38", label: "Cost / conv.", sub: "From launch" },
    ],
  },
];

const websites: { name: string; shot: string }[] = [
  { name: "Attorney", shot: siteAttorney },
  { name: "Barbershop", shot: siteBarbershop },
  { name: "Chiropractor", shot: siteChiropractor },
  { name: "Ready Roll-Off Dumpsters", shot: siteDumpster },
  { name: "Food Truck", shot: siteFoodtruck },
  { name: "Gym", shot: siteGym },
  { name: "Health & Wellness", shot: siteWellness },
  { name: "HVAC", shot: siteHvac },
  { name: "Installation Company", shot: siteInstallation },
  { name: "Insurance Agent", shot: siteInsurance },
  { name: "Lawn Care", shot: siteLawncare },
  { name: "Mortgage", shot: siteMortgage },
  { name: "Moving Company", shot: siteMoving },
  { name: "Stump Grinding", shot: siteStump },
  { name: "Therapist", shot: siteTherapist },
  { name: "Tiny Homes", shot: siteTinyhomes },
  { name: "Travel Planner", shot: siteTravel },
  { name: "Tree Service", shot: siteTree },
  { name: "Window Cleaner", shot: siteWindow },
  { name: "Winery & Event Space", shot: siteWinery },
];

const THRESHOLDS = [0, 0, 0.34, 0.58, 0.82];

type TabKey = "seo" | "ads" | "web";

function setFromHash(): TabKey | null {
  if (typeof window === "undefined") return null;
  const h = (window.location.hash || "").replace("#", "").toLowerCase();
  if (h === "ads") return "ads";
  if (h === "websites" || h === "web") return "web";
  if (h === "seo") return "seo";
  return null;
}

const Chapter = ({ study, n }: { study: CaseStudy; n: number }) => {
  const dark = n % 2 === 1;
  const bg = dark ? NAVY : CREAM;
  const headColor = dark ? CREAM : NAVY;
  const bodyColor = dark ? "rgba(247,243,236,0.82)" : "rgba(17,33,47,0.78)";
  const subColor = dark ? "rgba(247,243,236,0.6)" : "rgba(17,33,47,0.55)";
  const lineColor = dark ? "rgba(247,243,236,0.16)" : "rgba(14,40,70,0.14)";
  const numColor = dark ? "rgba(247,243,236,0.4)" : "rgba(17,33,47,0.35)";
  const tagColor = dark ? ACCENT : "#9c5e15";
  const flip = dark && n % 4 === 3 ? " scaleX(-1)" : "";
  const side = n % 4 === 3 ? { right: -120 } : { left: -120 };
  const hasPanel = !!(study.maps || study.beforeStats || study.resultGrid || study.stats);
  const cardBg = dark ? "rgba(247,243,236,0.04)" : "rgba(255,255,255,0.9)";
  const cardBorder = dark ? "rgba(247,243,236,0.14)" : "rgba(14,40,70,0.12)";

  return (
    <section id={`ch${n}`} data-chapter={n} data-track className="relative" style={{ height: "480vh", background: bg }}>
      <div data-sticky className="sticky top-0 h-screen flex items-center overflow-hidden px-6 md:px-14">
        {dark && (
          <div className="absolute pointer-events-none" style={{ ...side, top: "50%", transform: `translateY(-50%)${flip}`, width: 520, maxWidth: "50vw", opacity: 0.05 }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" />
          </div>
        )}

        {hasPanel && (
          <div data-panel className="bh-mappanel hidden md:block absolute right-14 top-1/2 -translate-y-1/2 z-[2]" style={{ width: "min(440px,34vw)" }}>
            {study.maps && (
              <>
                <div data-mapimg="problem" className="transition-opacity duration-500" style={{ opacity: 0 }}>
                  <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${cardBorder}`, boxShadow: `0 24px 60px rgba(0,0,0,${dark ? ".4" : ".14"})` }}>
                    <img src={study.maps[0]} alt="Local ranking grid before" className="w-full h-auto block" />
                  </div>
                  <div className="mt-3 font-bold text-[12.5px] tracking-[0.14em] uppercase" style={{ color: subColor }}>Before · Local grid rankings</div>
                </div>
                <div data-mapimg="solution" className="absolute left-0 right-0 top-0 transition-opacity duration-500" style={{ opacity: 0 }}>
                  <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${cardBorder}`, boxShadow: `0 24px 60px rgba(0,0,0,${dark ? ".4" : ".14"})` }}>
                    <img src={study.maps[1]} alt="Local ranking grid after" className="w-full h-auto block" />
                  </div>
                  <div className="mt-3 font-bold text-[12.5px] tracking-[0.14em] uppercase" style={{ color: tagColor }}>After · Local grid rankings</div>
                </div>
              </>
            )}
            {study.beforeStats && (
              <>
                <div data-mapimg="problem" className="transition-opacity duration-500" style={{ opacity: 0 }}>
                  <div className="rounded-2xl px-[30px] pt-[30px] pb-1.5" style={{ background: cardBg, border: `1px solid ${cardBorder}`, boxShadow: `0 24px 60px rgba(0,0,0,${dark ? ".4" : ".14"})` }}>
                    <div className="font-extrabold text-xs tracking-[0.14em] uppercase mb-[22px]" style={{ color: subColor }}>Before · March 2025</div>
                    <div className="grid grid-cols-2 gap-x-[18px] gap-y-[22px]">
                      {study.beforeStats.map((s) => (
                        <div key={s.label}>
                          <div className="font-black leading-none tracking-[-0.02em] mb-1.5" style={{ color: subColor, fontSize: "clamp(24px,2.2vw,30px)" }}>{s.value}</div>
                          <div className="text-[12.5px] font-semibold" style={{ color: subColor }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 font-bold text-[12.5px] tracking-[0.14em] uppercase" style={{ color: subColor }}>Google Ads · Account snapshot</div>
                </div>
                <div data-mapimg="solution" className="absolute left-0 right-0 top-0 transition-opacity duration-500" style={{ opacity: 0 }}>
                  <div className="rounded-2xl px-[30px] pt-[30px] pb-1.5" style={{ background: cardBg, border: `1px solid ${cardBorder}`, boxShadow: `0 24px 60px rgba(0,0,0,${dark ? ".4" : ".14"})` }}>
                    <div className="font-extrabold text-xs tracking-[0.14em] uppercase mb-[22px]" style={{ color: tagColor }}>After · March 2026</div>
                    <div className="grid grid-cols-2 gap-x-[18px] gap-y-[22px]">
                      {study.afterStats!.map((s) => (
                        <div key={s.label}>
                          <div className="font-black leading-none tracking-[-0.02em] mb-1.5" style={{ color: headColor, fontSize: "clamp(24px,2.2vw,30px)" }}>{s.value}</div>
                          <div className="text-[12.5px] font-semibold" style={{ color: subColor }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 font-bold text-[12.5px] tracking-[0.14em] uppercase" style={{ color: tagColor }}>Google Ads · Account snapshot</div>
                </div>
              </>
            )}
            {study.resultGrid && (
              <div data-mapimg="result" className="absolute left-0 right-0 top-1/2 -translate-y-1/2 transition-opacity duration-500" style={{ opacity: 0 }}>
                <div className="rounded-2xl px-[30px] pt-[30px] pb-1.5" style={{ background: cardBg, border: `1px solid ${cardBorder}`, boxShadow: `0 24px 60px rgba(0,0,0,${dark ? ".4" : ".14"})` }}>
                  <div className="font-extrabold text-xs tracking-[0.14em] uppercase mb-6" style={{ color: tagColor }}>
                    {study.resultGridLabel || (study.beforeStats ? "Year over year" : "Since launch")}
                  </div>
                  <div className="grid grid-cols-2 gap-x-[18px] gap-y-6">
                    {study.resultGrid.map((s) => (
                      <div key={s.label}>
                        <div className="font-black leading-none tracking-[-0.02em] mb-1.5" style={{ color: tagColor, fontSize: "clamp(26px,2.4vw,34px)" }}>{s.value}</div>
                        <div className="text-[13px] font-bold mb-0.5" style={{ color: headColor }}>{s.label}</div>
                        <div className="text-[12.5px] font-semibold" style={{ color: subColor }}>{s.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-3 font-bold text-[12.5px] tracking-[0.14em] uppercase" style={{ color: tagColor }}>Google Ads · Results</div>
              </div>
            )}
            {study.stats && (
              <div data-mapimg="result" className="absolute left-0 right-0 top-1/2 -translate-y-1/2 transition-opacity duration-500" style={{ opacity: 0 }}>
                <div className="rounded-2xl px-[30px] pt-[30px]" style={{ background: cardBg, border: `1px solid ${cardBorder}`, boxShadow: `0 24px 60px rgba(0,0,0,${dark ? ".4" : ".14"})` }}>
                  {study.stats.map((st, si) => (
                    <div key={st.label} className="pb-6 mb-6" style={{ borderBottom: si === study.stats!.length - 1 ? "none" : `1px solid ${dark ? "rgba(247,243,236,0.1)" : "rgba(14,40,70,0.08)"}` }}>
                      <div className="font-black leading-none tracking-[-0.02em] mb-2" style={{ color: headColor, fontSize: "clamp(38px,3.6vw,52px)" }}>{st.value}</div>
                      <div className="text-[15px] leading-snug mb-2" style={{ color: bodyColor }}>{st.label}</div>
                      <div className="font-extrabold text-sm flex items-baseline gap-1.5" style={{ color: tagColor }}>
                        ↑ {st.delta} <span className="font-semibold text-[12.5px]" style={{ color: subColor }}>{st.period}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 font-bold text-[12.5px] tracking-[0.14em] uppercase" style={{ color: tagColor }}>Google Business Profile · Performance</div>
                {study.quote && (
                  <div className="bh-quotecard mt-[18px] rounded-2xl px-6 py-[22px]" style={{ border: `1px solid ${cardBorder}`, background: cardBg }}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <span style={{ color: ACCENT, fontSize: 15, letterSpacing: "0.08em" }}>★★★★★</span>
                      <span className="font-extrabold text-[13.5px]" style={{ color: headColor }}>{study.quote.name}</span>
                    </div>
                    <p className="text-[14.5px] leading-relaxed m-0" style={{ color: bodyColor }}>{study.quote.text}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="max-w-[1080px] mx-auto w-full relative z-[1]">
          <div data-step="0" className="transition-[opacity,transform] duration-700" style={{ transitionTimingFunction: "cubic-bezier(.16,.8,.3,1)" }}>
            <div className="flex items-center justify-between gap-5 flex-wrap mb-5">
              <span className="inline-flex items-center gap-3 font-extrabold text-[13px] tracking-[0.14em] uppercase" style={{ color: tagColor }}>
                <span className="w-[26px] h-[3px] rounded-sm inline-block" style={{ background: ACCENT }} />
                {study.tag}
              </span>
              <span className="font-extrabold text-sm" style={{ letterSpacing: "0.1em", color: numColor }}>0{n} / 04</span>
            </div>
            <h2 className="font-black leading-none tracking-[-0.025em] m-0" style={{ color: headColor, fontSize: "clamp(34px,5.2vw,68px)" }}>{study.title}</h2>
          </div>

          <div data-steps className="bh-steps mt-11" style={{ maxWidth: hasPanel ? "min(720px, calc(100% - 500px))" : "720px" }}>
            {(["situation", "problem", "solution"] as const).map((field, i) => (
              <div key={field} data-step={i + 1} className="grid grid-cols-[34px_1fr] gap-[22px] items-start transition-[opacity,transform] duration-700" style={{ transitionTimingFunction: "cubic-bezier(.16,.8,.3,1)" }}>
                <div className="relative flex justify-center pt-1">
                  <div data-line className="absolute w-0.5" style={{ top: 14, bottom: -44, background: lineColor }} />
                  <div data-dot data-dotbg={bg} className="relative z-[1] w-4 h-4 rounded-full transition-colors duration-500" style={{ border: `2px solid ${ACCENT}`, background: bg }} />
                </div>
                <div className="pb-11">
                  <div className="font-extrabold text-[12.5px] tracking-[0.16em] uppercase mb-2.5" style={{ color: tagColor }}>
                    {field === "situation" ? "Situation" : field === "problem" ? "Problem" : "Solution"}
                  </div>
                  <p className="m-0" style={{ color: bodyColor, fontSize: "clamp(17px,1.7vw,21px)", lineHeight: 1.6, maxWidth: 560 }}>{study[field]}</p>
                </div>
              </div>
            ))}
            <div data-step="4" className="grid grid-cols-[34px_1fr] gap-[22px] items-start transition-[opacity,transform] duration-700" style={{ transitionTimingFunction: "cubic-bezier(.16,.8,.3,1)" }}>
              <div className="relative flex justify-center pt-1">
                <div data-dot data-dotbg={bg} className="relative z-[1] w-4 h-4 rounded-full transition-colors duration-500" style={{ border: `2px solid ${ACCENT}`, background: bg }} />
              </div>
              <div>
                <div className="font-extrabold text-[12.5px] tracking-[0.16em] uppercase mb-1.5" style={{ color: tagColor }}>Result</div>
                <div data-count data-to={study.to} data-prefix={study.prefix} data-suffix={study.suffix} className="font-black leading-[0.9] tracking-[-0.02em] my-0.5 mb-3" style={{ color: ACCENT, fontSize: "clamp(52px,8vw,104px)" }}>
                  {study.prefix}0{study.suffix}
                </div>
                <p className="m-0" style={{ color: subColor, fontSize: "clamp(15px,1.5vw,18px)", maxWidth: 520 }}>{study.resultLabel}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CaseStudies = () => {
  const location = useLocation();
  const [activeSet, setActiveSet] = useState<TabKey>(() => setFromHash() || "seo");
  const containerRef = useRef<HTMLDivElement>(null);
  const chaptersRef = useRef<HTMLDivElement>(null);

  // Sync with URL hash (header dropdown deep links). React Router's <Link>
  // navigates via history.pushState, which does not fire the native
  // "hashchange" event, so watch location.hash (which React Router does
  // update reactively) instead of relying on that browser event.
  useEffect(() => {
    const s = setFromHash();
    if (s && s !== activeSet) setActiveSet(s);
  }, [location.hash, activeSet]);

  const selectTab = (tab: TabKey) => {
    setActiveSet(tab);
    history.replaceState(null, "", tab === "seo" ? "#seo" : tab === "ads" ? "#ads" : "#websites");
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const target = chaptersRef.current;
        if (target) {
          const y = Math.max(0, target.getBoundingClientRect().top + window.scrollY - 4);
          window.scrollTo({ top: y, behavior: "auto" });
        }
      })
    );
  };

  // Pinned-scroll driver: runs on mount and whenever the active tab changes
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mobile = window.matchMedia("(max-width:880px)").matches;
    const skip = rm || mobile;

    const countUp = (el: HTMLElement) => {
      const to = parseFloat(el.getAttribute("data-to") || "0");
      const suf = el.getAttribute("data-suffix") || "";
      const pre = el.getAttribute("data-prefix") || "";
      const key = pre + to + suf;
      if (el.dataset.done === key) return;
      el.dataset.done = key;
      const dur = 1400;
      const start = performance.now();
      const step = (t: number) => {
        const p = Math.min(1, (t - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = pre + Math.round(to * eased).toLocaleString() + suf;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const tracks = Array.from(root.querySelectorAll<HTMLElement>("[data-track]"));
    const topBtn = document.getElementById("csTop");
    const prog = document.getElementById("csProgress");
    const bars = Array.from(root.querySelectorAll<HTMLElement>("[data-pdot]"));

    const setActiveBar = (idx: number) => {
      bars.forEach((b) => {
        const on = Number(b.getAttribute("data-pdot")) === idx;
        const bar = b.querySelector<HTMLElement>("[data-pdotbar]");
        if (!bar) return;
        bar.style.height = on ? "34px" : "16px";
        bar.style.background = on ? ACCENT : "rgba(140,150,160,.55)";
      });
    };

    if (skip) {
      root.querySelectorAll<HTMLElement>("[data-step]").forEach((el) => {
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      root.querySelectorAll<HTMLElement>("[data-dot]").forEach((d) => {
        d.style.background = ACCENT;
      });
      root.querySelectorAll<HTMLElement>("[data-count]").forEach((el) => {
        const to = parseFloat(el.getAttribute("data-to") || "0");
        const suf = el.getAttribute("data-suffix") || "";
        const pre = el.getAttribute("data-prefix") || "";
        el.textContent = pre + to.toLocaleString() + suf;
      });
      tracks.forEach((t) => {
        t.style.height = "auto";
        const sticky = t.querySelector<HTMLElement>("[data-sticky]");
        if (sticky) {
          sticky.style.position = "static";
          sticky.style.height = "auto";
          sticky.style.padding = "88px 24px";
        }
        const panel = t.querySelector<HTMLElement>("[data-panel]");
        if (panel) panel.style.display = "none";
      });
      if (prog) prog.style.display = "none";
      if (topBtn) topBtn.style.display = "none";
      return;
    }

    root.querySelectorAll<HTMLElement>("[data-step]").forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(26px)";
    });
    if (prog) prog.style.display = tracks.length ? "flex" : "none";

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      let pinned = 0;
      tracks.forEach((track) => {
        const r = track.getBoundingClientRect();
        const total = r.height - vh;
        let p = total > 0 ? -r.top / total : r.top <= 0 ? 1 : 0;
        p = Math.max(0, Math.min(1, p));
        const steps = track.querySelectorAll<HTMLElement>("[data-step]");
        steps.forEach((it) => {
          const k = Number(it.getAttribute("data-step"));
          const on = p >= (THRESHOLDS[k] ?? 0) - 0.0001;
          it.style.opacity = on ? "1" : "0";
          it.style.transform = on ? "none" : "translateY(26px)";
          const dot = it.querySelector<HTMLElement>("[data-dot]");
          if (dot) dot.style.background = on ? ACCENT : dot.getAttribute("data-dotbg") || "transparent";
          if (on && k === 4) {
            const c = it.querySelector<HTMLElement>("[data-count]");
            if (c) countUp(c);
          }
        });
        const mp = track.querySelector<HTMLElement>('[data-mapimg="problem"]');
        const ms = track.querySelector<HTMLElement>('[data-mapimg="solution"]');
        if (mp && ms) {
          const mr = track.querySelector<HTMLElement>('[data-mapimg="result"]');
          const showResult = !!mr && p >= THRESHOLDS[4] - 0.0001;
          const showAfter = !showResult && p >= THRESHOLDS[3] - 0.0001;
          const showAny = p >= THRESHOLDS[2] - 0.0001;
          mp.style.opacity = showAny && !showAfter && !showResult ? "1" : "0";
          ms.style.opacity = showAfter ? "1" : "0";
          if (mr) mr.style.opacity = showResult ? "1" : "0";
        } else {
          const mr = track.querySelector<HTMLElement>('[data-mapimg="result"]');
          if (mr) mr.style.opacity = p >= THRESHOLDS[4] - 0.0001 ? "1" : "0";
        }
        if (r.top <= 1 && r.bottom > vh) pinned = Number(track.getAttribute("data-chapter"));
      });
      if (prog) prog.style.opacity = pinned ? "1" : "0";
      if (topBtn) {
        topBtn.style.opacity = pinned ? "1" : "0";
        topBtn.style.pointerEvents = pinned ? "auto" : "none";
        topBtn.style.transform = pinned ? "none" : "translateY(10px)";
      }
      if (pinned) setActiveBar(pinned);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [activeSet]);

  const chapters = activeSet === "web" ? [] : activeSet === "ads" ? adsStudies : seoStudies;

  return (
    <Layout active="case-studies" background={NAVY}>
      <div ref={containerRef} style={{ fontFamily: "'Archivo', sans-serif", background: NAVY }} className="bh-case-studies w-full antialiased">
        <Seo
          title="Case Studies | Blue Heron Marketing"
          description="Real results for real local businesses: local SEO, Google Ads, and website case studies from Blue Heron Marketing."
          path="/case-studies"
        />

        {/* Hero */}
        <section id="top" className="relative overflow-hidden px-6 md:px-14 py-[120px]" style={{ background: NAVY }}>
          <div className="absolute -right-[140px] -bottom-10 pointer-events-none" style={{ width: 600, maxWidth: "55vw", opacity: 0.06, transform: "scaleX(-1)" }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" style={{ animation: "bhFloat 11s ease-in-out infinite" }} />
          </div>
          <div className="max-w-[1280px] mx-auto relative z-10">
            <Eyebrow dark>Case Studies</Eyebrow>
            <h1 className="font-black leading-[0.98] tracking-[-0.02em] mb-6 max-w-[820px]" style={{ color: CREAM, fontSize: "clamp(40px,6vw,80px)" }}>
              Real results for real businesses.
            </h1>
            <p className="max-w-[640px] mb-8 leading-relaxed" style={{ color: "rgba(247,243,236,0.78)", fontSize: "clamp(17px,1.6vw,20px)" }}>
              Every project follows the same path: understand the situation, name the real problem, build the solution, then measure the result. Pick the service you care about and scroll through four of them.
            </p>
            <div className="inline-flex items-center gap-1.5 rounded-full p-1.5 mb-8" style={{ background: "rgba(247,243,236,0.06)", border: "1px solid rgba(247,243,236,0.12)" }}>
              {(["seo", "ads", "web"] as TabKey[]).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => selectTab(tab)}
                  className="inline-flex items-center font-extrabold text-[15px] px-[26px] py-3 rounded-full border-none cursor-pointer transition-all"
                  style={{
                    background: activeSet === tab ? ACCENT : "transparent",
                    color: activeSet === tab ? NAVY : "rgba(247,243,236,0.7)",
                    fontFamily: "inherit",
                  }}
                >
                  {tab === "seo" ? "Local SEO" : tab === "ads" ? "Google Ads" : "Websites"}
                </button>
              ))}
            </div>
            <div>
              <a
                href="#ch1"
                className="inline-flex items-center gap-2.5 font-extrabold text-base px-8 py-[18px] rounded-full no-underline transition-transform hover:-translate-y-0.5"
                style={{ background: ACCENT, color: NAVY, boxShadow: "0 14px 30px rgba(0,0,0,.22)" }}
              >
                Start Reading ↓
              </a>
            </div>
          </div>
        </section>

        <a
          id="csTop"
          href="#top"
          title="Back to top"
          className="fixed left-7 bottom-7 z-[41] w-[52px] h-[52px] rounded-full flex items-center justify-center no-underline"
          style={{ background: ACCENT, boxShadow: "0 10px 26px rgba(0,0,0,.28)", opacity: 0, pointerEvents: "none", transform: "translateY(10px)", transition: "opacity .35s ease, transform .35s ease" }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#081627" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><path d="m6 15 6-6 6 6" /></svg>
        </a>

        <div id="csProgress" className="fixed right-[30px] top-1/2 -translate-y-1/2 z-40 hidden flex-col gap-3.5" style={{ opacity: 0, transition: "opacity .4s ease", pointerEvents: "none" }}>
          {[1, 2, 3, 4].map((n) => (
            <a key={n} href={`#ch${n}`} data-pdot={n} className="flex justify-end" style={{ pointerEvents: "auto" }}>
              <span data-pdotbar className="block w-1 rounded-[3px]" style={{ height: 16, background: "rgba(140,150,160,.55)", transition: "height .3s ease, background .3s ease" }} />
            </a>
          ))}
        </div>

        <div ref={chaptersRef} id="csChapters">
          {activeSet === "web" && (
            <section className="px-6 md:px-14 py-[110px]" style={{ background: CREAM }}>
              <div className="max-w-[1280px] mx-auto">
                <Eyebrow>Websites</Eyebrow>
                <h2 className="font-black leading-none tracking-[-0.025em] mb-4 max-w-[760px]" style={{ color: NAVY, fontSize: "clamp(32px,4.4vw,56px)" }}>
                  Built for local businesses across every trade.
                </h2>
                <p className="max-w-[600px] mb-14 text-lg leading-relaxed" style={{ color: "rgba(17,33,47,0.65)" }}>
                  Fast, clear sites designed to turn a search into a phone call. Here is a sample of the industries I build for.
                </p>
                <div className="bh-webgrid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {websites.map((w) => (
                    <div
                      key={w.name}
                      className="bg-white rounded-2xl overflow-hidden transition-transform duration-300 cursor-default"
                      style={{ border: "1px solid rgba(14,40,70,0.12)" }}
                    >
                      <div className="overflow-hidden" style={{ aspectRatio: "16/11", background: "rgba(14,40,70,0.05)" }}>
                        <img src={w.shot} alt={`${w.name} website`} className="w-full h-full object-cover" style={{ objectPosition: "top center" }} />
                      </div>
                      <div className="px-[22px] py-5 flex items-center justify-between gap-3">
                        <span className="font-extrabold text-base" style={{ color: NAVY, letterSpacing: "-0.01em" }}>{w.name}</span>
                        <span className="font-black text-[15px]" style={{ color: ACCENT }}>↗</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {chapters.map((study, i) => (
            <Chapter key={`${activeSet}-${study.title}`} study={study} n={i + 1} />
          ))}
        </div>

        {/* Closing CTA */}
        <section className="px-6 md:px-14 py-[130px] text-center" style={{ background: NAVY, borderTop: "1px solid rgba(247,243,236,0.08)" }}>
          <div className="max-w-[680px] mx-auto">
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-5" style={{ color: CREAM, fontSize: "clamp(30px,4.4vw,52px)" }}>
              Ready to be the next success story?
            </h2>
            <p className="mb-10 text-lg leading-relaxed" style={{ color: "rgba(247,243,236,0.65)" }}>Let's talk about what's possible for your business.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/book" className="inline-flex items-center gap-2.5 font-extrabold text-base px-8 py-[18px] rounded-full no-underline transition-transform hover:-translate-y-0.5" style={{ background: ACCENT, color: NAVY }}>
                Book a Call →
              </Link>
              <Link to="/#services" className="inline-flex items-center gap-2.5 font-bold text-base px-[30px] py-[18px] rounded-full no-underline transition-colors" style={{ background: "transparent", color: CREAM, border: "1.5px solid rgba(247,243,236,.35)" }}>
                View Services
              </Link>
            </div>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bh-case-studies h1, .bh-case-studies h2, .bh-case-studies h3 {
          font-family: 'Archivo', sans-serif;
        }
        @keyframes bhFloat {
          0% { transform: translateY(0) rotate(-6deg); }
          50% { transform: translateY(-22px) rotate(-4deg); }
          100% { transform: translateY(0) rotate(-6deg); }
        }
        @media (max-width: 880px) {
          .bh-case-studies #csProgress { display: none !important; }
          .bh-webgrid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 1280px) {
          .bh-mappanel { width: min(360px,34vw) !important; right: 24px !important; }
        }
        @media (max-height: 760px) {
          .bh-quotecard { display: none !important; }
        }
      ` }} />
    </Layout>
  );
};

export default CaseStudies;
